// Chemin: C:\gestion-copro\src\utils\courrierComptableUtils.ts
// Session 70 : Utilitaires pour générer des courriers depuis le module Vérification Comptable
// Intégration Courriers SCUD ↔ Vérification Comptable

import { Controle, TYPE_CONTROLE_LABELS } from '../types/verification.types';

/**
 * Génère l'objet d'un courrier de contestation comptable
 */
export function genererObjetContestationComptable(
  controle: Controle,
  exercice: string
): string {
  const typeLabel = TYPE_CONTROLE_LABELS[controle.type] || controle.type;
  return `Demande de justificatifs - ${typeLabel} - Exercice ${exercice}`;
}

/**
 * Génère le corps d'un courrier de contestation comptable
 * Format professionnel et factuel (méthode SCUD)
 */
export function genererContenuContestationComptable(
  controle: Controle,
  exercice: string,
  nomCopropriete: string
): string {
  const typeLabel = TYPE_CONTROLE_LABELS[controle.type] || controle.type;
  
  // Formatage des montants
  const formatMontant = (montant: number | undefined): string => {
    if (montant === undefined) return 'N/A';
    return montant.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
  };

  // Construction du corps du courrier
  let contenu = `Madame, Monsieur,

Dans le cadre de notre mission de controle des comptes de la copropriete ${nomCopropriete} pour l'exercice ${exercice}, nous avons procede a une verification des documents comptables que vous nous avez transmis.

A l'issue de cette analyse, nous avons identifie un point necessitant des eclaircissements de votre part :

**Nature du point souleve :** ${typeLabel}

**Description :**
${controle.description}`;

  // Ajouter les montants si disponibles
  if (controle.montant !== undefined) {
    contenu += `

**Montant constate :** ${formatMontant(controle.montant)}`;
    
    if (controle.montantAttendu !== undefined) {
      contenu += `
**Montant attendu :** ${formatMontant(controle.montantAttendu)}`;
    }
    
    if (controle.ecart !== undefined) {
      contenu += `
**Ecart :** ${formatMontant(controle.ecart)}`;
      
      if (controle.pourcentageEcart !== undefined) {
        contenu += ` (${controle.pourcentageEcart.toFixed(1)}%)`;
      }
    }
  }

  // Ajouter la règle appliquée si disponible
  if (controle.regleAppliquee) {
    contenu += `

**Regle de controle appliquee :**
${controle.regleAppliquee}`;
  }

  // Demande de justificatifs
  contenu += `

Conformement a l'article 21 de la loi du 10 juillet 1965, le conseil syndical a pour mission de controler la gestion du syndic. A ce titre, nous vous demandons de bien vouloir nous transmettre les justificatifs permettant d'expliquer ce point, et notamment :

- Les pieces comptables correspondantes (factures, releves, etc.)
- Toute explication ecrite permettant de comprendre la situation

Nous vous remercions de nous faire parvenir ces elements dans un delai de 15 jours a compter de la reception du present courrier.

Dans l'attente de votre retour, nous vous prions d'agreer, Madame, Monsieur, l'expression de nos salutations distinguees.

Le Conseil Syndical de la ${nomCopropriete}`;

  return contenu;
}

/**
 * Génère les données complètes pour créer un courrier de contestation
 */
export function preparerDonneesCourrierContestation(
  controle: Controle,
  exercice: string,
  nomCopropriete: string,
  coproprieteId: string,
  verificationId: string,
  redacteurId: string,
  redacteurNom: string
): {
  objet: string;
  contenu: string;
  type: 'demande_information';
  modeEnvoi: 'email';
  coproprieteId: string;
  redacteurId: string;
  redacteurNom: string;
  destinataire: {
    type: 'syndic';
    nom: string;
  };
  metadata: {
    source: 'verification_comptable';
    verificationId: string;
    controleId: string;
    exercice: string;
  };
} {
  return {
    objet: genererObjetContestationComptable(controle, exercice),
    contenu: genererContenuContestationComptable(controle, exercice, nomCopropriete),
    type: 'demande_information',
    modeEnvoi: 'email',
    coproprieteId,
    redacteurId,
    redacteurNom,
    destinataire: {
      type: 'syndic',
      nom: 'Syndic de copropriete'
    },
    metadata: {
      source: 'verification_comptable',
      verificationId,
      controleId: controle.id,
      exercice
    }
  };
}

/**
 * Génère le titre d'une affaire depuis un contrôle comptable
 */
export function genererTitreAffaireComptable(
  controle: Controle,
  exercice: string
): string {
  const typeLabel = TYPE_CONTROLE_LABELS[controle.type] || controle.type;
  return `Audit comptable - ${typeLabel} - ${exercice}`;
}

/**
 * Génère la description d'une affaire depuis un contrôle comptable
 */
export function genererDescriptionAffaireComptable(
  controle: Controle,
  exercice: string
): string {
  const typeLabel = TYPE_CONTROLE_LABELS[controle.type] || controle.type;
  
  let description = `Affaire ouverte suite a la detection d'une anomalie comptable lors de la verification de l'exercice ${exercice}.

**Type d'anomalie :** ${typeLabel}

**Description :**
${controle.description}`;

  if (controle.montant !== undefined) {
    const formatMontant = (m: number) => m.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
    description += `

**Montant concerne :** ${formatMontant(controle.montant)}`;
    
    if (controle.ecart !== undefined) {
      description += `
**Ecart detecte :** ${formatMontant(controle.ecart)}`;
    }
  }

  if (controle.regleAppliquee) {
    description += `

**Regle de controle :** ${controle.regleAppliquee}`;
  }

  description += `

**Actions recommandees :**
- Demander des justificatifs au syndic
- Documenter les echanges
- Presenter en AG si necessaire`;

  return description;
}